@if (confirmationPage) {
  <app-confirmation-page />
} @else {
  <div id="checkout" class="bg-gray-100 pb-28 h-full overflow-y-auto">
    <!--
      The back button lives in the header — see
      core/layout/header/header.component.html's `header-back-button`,
      which calls the same
      TransactionService.restartTransaction() flow. Summary has no back button at all (there's no
      further review step past it) — the header only shows this for
      `backBtn === 'checkout'`.
    -->
    <div class="mx-auto px-4 max-w-7xl container">
      <!--
        Two-column layout: form flow on the left, an Order Summary sidebar on the
        right that stays in view as the left column scrolls.
        `lg:sticky` is used here
        instead of `lg:fixed` + a computed `left` calc() tied to
        its own container width — same visual result (pins to the viewport while the left column
        scrolls) without needing a fragile calc() against #checkout's own
        max-w-7xl container, since `sticky` naturally stays inside its own
        column's horizontal bounds and its containing block is #checkout's
        own `overflow-y-auto` (no offset math for the header/footer needed).
      -->
      <div class="relative flex lg:flex-row flex-col gap-6">
        <!-- LEFT COLUMN -->
        <!--
          `flex flex-col gap-6` (not `space-y-6`) for the vertical rhythm:
          `space-y-*` adds `margin-top` to every child but the last, so when
          both conditional blocks above are absent "Your Cart" (the first
          rendered child) gets a stray 24px top margin and no longer sits
          flush with the right column's timer. A flex `gap` only ever lives
          BETWEEN rendered children — the 24px separation between
          `app-group-outing` and `app-rainout-info` is correct whether one,
          both, or neither renders, and the first rendered child always
          starts at the column's top edge.
        -->
        <div class="flex flex-col gap-6 w-full lg:w-2/3">
          <!--
            Need more Fun? (upsells) + rainout insurance — no wrapping card:
            each upsell is its own white rounded-24 card with
            its own shadow sitting directly on the page background, and the
            "Need more Fun?" heading sits on the background too.
          -->
          @if (
            configurationService.configuration.upsells &&
            transaction()?.transaction?.event?.upsells?.length
          ) {
            <app-group-outing
              [transaction]="transaction()!"
              [upsellSelected]="upsellSelected"
              [totalTransactionSeats]="totalTransactionSeats"
              [discountActive]="isDiscountActive"
              (changeUpsell)="onChangeUpsell($event)" />
          }
          @if (isRainout) {
            <div class="w-full">
              <app-rainout-info
                [transaction]="transaction()!"
                [totalTransactionSeats]="seats.length"
                [upsellSelected]="upsellSelected"
                (changeUpsell)="onChangeUpsell($event)"
                (rainoutChange)="onRainoutChange($event)" />
            </div>
          }

          <!--
            "Your Cart" is a section heading, not a page title —
            it sits directly above the Ticket details card, not spanning
            both columns, so the right column's Order Summary can start flush
            with the left column's Ticket details card (same top edge).
            No own margin: the parent's `gap-6` already gives it 24px
            above (from the section before it, whichever renders) and below
            (to the Ticket details card) — an `mb-4` here would stack with
            that and double the gap below.
          -->
          <h2 class="font-extrabold text-2xl">Your Cart</h2>

          <!-- Ticket details -->
          <div class="w-full">
            <app-cart class="flex h-full" [transaction]="transaction()!" />
          </div>

          <form #paymentForm="ngForm">
            <!--
              Customer Information: patron
              login/account-credit and the guest name/address/email/phone
              fields are a separate "who you are" step from "how you pay"
              below, instead of living inside the Payment Options card.
            -->
            <div class="mb-6 px-4 sm:px-8 py-6 rounded-3xl w-full box-white">
              <h3 class="mb-4 font-bold">Customer Information</h3>
              <div
                class="flex sm:flex-row flex-col justify-end items-center mb-4">
                <app-okta-container
                  (patronEmmiter)="
                    oktaEventEmitter($event)
                  "></app-okta-container>
              </div>

              @if (patron) {
                <div class="mb-4">
                  <app-patron
                    (blurInputCredits)="onBlurInputCredits()"
                    (changeDigitalWalletSelector)="
                      onChangeDigitalWalletSelector()
                    "
                    (checkboxPayInFullChange)="checkboxPayInFullChange($event)"
                    [patron]="patron"
                    (addDigitalWalletClick)="onAddDigitalWallet()"
                    [paymentDetails]="paymentDetails"
                    [showCreditsApplies]="showCreditsApplies"
                    [substractCreditsToBalance]="substractCreditsToBalance"
                    [transaction]="transaction()!" />
                </div>
              }

              <div class="mb-2 sm:mb-0">
                <div class="sm:flex sm:mb-4">
                  <div class="w-full">
                    <label class="label" for="first-name"
                      >First name
                      <span class="text-red align-middle" aria-hidden="true"
                        >*</span
                      ></label
                    >
                    <input
                      type="text"
                      class="input"
                      placeholder="First name"
                      id="first-name"
                      name="first-name"
                      [(ngModel)]="paymentDetails.firstName"
                      [readOnly]="patron"
                      [attr.aria-required]="true" />
                  </div>
                  <div class="sm:ml-6 w-full">
                    <label class="label" for="last-name"
                      >Last name
                      <span class="text-red align-middle" aria-hidden="true"
                        >*</span
                      ></label
                    >
                    <input
                      type="text"
                      class="input"
                      placeholder="Last name"
                      id="last-name"
                      name="last-name"
                      [(ngModel)]="paymentDetails.lastName"
                      [readOnly]="patron"
                      [attr.aria-required]="true" />
                  </div>
                </div>
                @if (
                  configuration.custom.paymentPage.captureAddress &&
                  paymentDetails.address
                ) {
                  <!-- Row 1: Address, City (order: Address, City, State, Postal Code, Country) -->
                  <div class="sm:flex sm:mb-4">
                    <div class="w-full">
                      <label class="label" for="address"
                        >Address
                        <span class="text-red align-middle" aria-hidden="true"
                          >*</span
                        >
                      </label>
                      <input
                        type="text"
                        class="input"
                        placeholder="Line 1"
                        id="address"
                        name="address"
                        [(ngModel)]="paymentDetails.address.address1"
                        [readOnly]="patron && patronAddressLocked"
                        [attr.aria-required]="true" />
                    </div>
                    <div class="my-3 sm:my-0 sm:ml-6 w-full">
                      <label class="label" for="city"
                        >City
                        <span class="text-red align-middle" aria-hidden="true"
                          >*</span
                        >
                      </label>
                      <input
                        type="text"
                        class="input"
                        placeholder="Boston"
                        id="city"
                        name="city"
                        [(ngModel)]="paymentDetails.address.city"
                        [readOnly]="patron && patronAddressLocked"
                        [attr.aria-required]="true" />
                    </div>
                  </div>
                  <!-- Row 2: State, Postal Code, Country -->
                  <div class="xs:flex xs:flex-wrap">
                    <div
                      class="mb-4 w-full xs:w-1/2 sm:w-1/3 md:w-1/2 lg:w-1/3">
                      <label class="label" for="SubCountryCode"
                        >State
                        <span class="text-red align-middle" aria-hidden="true"
                          >*</span
                        >
                      </label>
                      <select
                        type="text"
                        class="bg-white input"
                        [disabled]="patron && patronAddressLocked"
                        id="SubCountryCode"
                        name="SubCountryCode"
                        [(ngModel)]="paymentDetails.address.subCountryCode"
                        [attr.aria-required]="true">
                        @for (
                          item of subCountryList;
                          track item.sub_country_code
                        ) {
                          <option [value]="item.sub_country_code">
                            {{ item.sub_country_name }}
                          </option>
                        }
                      </select>
                    </div>
                    <div
                      class="mb-4 w-full xs:w-1/2 sm:w-1/3 md:w-1/2 lg:w-1/3">
                      <div class="xs:pl-6">
                        <label class="label" for="postalCode"
                          >Postal Code
                          <span class="text-red" aria-hidden="true">*</span>
                        </label>
                        <input
                          type="text"
                          class="input"
                          placeholder="Postal Code"
                          id="postalCode"
                          name="postalCode"
                          [(ngModel)]="paymentDetails.address.postalCode"
                          [readOnly]="patron && patronAddressLocked"
                          [attr.aria-required]="true" />
                      </div>
                    </div>
                    <div class="mb-4 w-full sm:w-1/3 md:w-full lg:w-1/3">
                      <div class="sm:pl-6 md:pl-0 lg:pl-6">
                        <label class="label" for="countryCode"
                          >Country
                          <span class="text-red align-middle" aria-hidden="true"
                            >*</span
                          >
                        </label>
                        <select
                          type="text"
                          [disabled]="patron && patronAddressLocked"
                          class="bg-white input"
                          (ngModelChange)="onChangeAddressCountry($event)"
                          id="countryCode"
                          name="countryCode"
                          [(ngModel)]="paymentDetails.address.countryCode"
                          [attr.aria-required]="true">
                          @for (item of countryList; track item.icaoCode) {
                            <option [value]="item.icaoCode">
                              {{ item.name }}
                            </option>
                          }
                        </select>
                      </div>
                    </div>
                  </div>
                }
                <div class="mb-3 md:mb-4">
                  <div class="w-full">
                    <label class="label" for="email"
                      >Email
                      <span class="text-red align-middle" aria-hidden="true"
                        >*</span
                      ></label
                    >
                    <input
                      type="email"
                      class="input"
                      placeholder="example@gmail.com"
                      id="email"
                      name="email"
                      [(ngModel)]="paymentDetails.email"
                      email
                      [readOnly]="patron"
                      [attr.aria-required]="true" />
                  </div>
                  <p class="mt-1">
                    *Tickets will be delivered to this email in the MLB Ballpark
                    App.
                    <button
                      class="inline-flex bg-main disabled:opacity-30 rounded-full w-4 h-4 text-white disabled:hover:transform-none hover:scale-95 transition-transform btn-icon"
                      (click)="infoEmailModal()">
                      i
                    </button>
                  </p>
                </div>
                @if (
                  configuration.custom.paymentPage.capturePhone &&
                  paymentDetails.phone
                ) {
                  <div class="flex mb-4 md:mb-0">
                    <div class="flex-shrink-0 mr-2 md:mr-4">
                      <label class="label" for="phone-country-code"
                        >Country Code
                        <span class="text-red align-middle" aria-hidden="true"
                          >*</span
                        >
                      </label>
                      <select
                        type="text"
                        [disabled]="patron && patronPhoneLocked"
                        class="bg-white input"
                        id="phone-country-code"
                        name="phoneCountryCode"
                        [(ngModel)]="paymentDetails.phone.countryCode"
                        [attr.aria-required]="true">
                        @for (item of countryList; track item.icaoCode) {
                          <option [value]="item.icaoCode">
                            +{{ item.phoneCode }} ({{ item.icaoCode }})
                          </option>
                        }
                      </select>
                    </div>
                    <div class="w-full flex-1">
                      <label class="label" for="phone"
                        >Phone
                        <span class="text-red align-middle" aria-hidden="true"
                          >*</span
                        >
                      </label>
                      <input
                        type="text"
                        class="input"
                        placeholder="XXXXXXXX"
                        id="phone"
                        name="phone"
                        [(ngModel)]="paymentDetails.phone.phone"
                        [readOnly]="patron && patronPhoneLocked"
                        [attr.aria-required]="true" />
                    </div>
                  </div>
                }
              </div>
            </div>

            <!--
              Payment Options — its own card, separate from Customer
              Information above: total-due recap, then
              the payment method (Braintree) entry, then the legal-terms
              checkbox.
            -->
            <!--
              This card is deliberately the one with a larger radius here
              (32px, vs the base 24px `rounded-3xl` every other card uses) —
              do not "fix" it back to match its siblings.
            -->
            <div class="px-4 sm:px-8 py-6 rounded-[32px] w-full box-white">
              <!--
                `h3`'s base rule already supplies `text-xl`/`font-semibold`
                (see @layer base); this heading is 22px/800, so both
                the size and weight need an explicit override here.
              -->
              <h3 class="mb-4 font-extrabold text-[22px]">Payment Options</h3>

              <!-- Total amount due recap -->
              <div class="mb-4">
                <app-checkout-recap
                  section="totals"
                  [transaction]="transaction()!"
                  [paymentDetails]="paymentDetails"
                  [isTicketFeeEnabled]="isTicketFeeEnabled()"
                  [isDiscountCodesEnabled]="isDiscountCodesEnabled()" />
              </div>

              <p class="mb-2 label">
                Payment method
                <span class="text-red align-middle" aria-hidden="true">*</span>
              </p>
              <!--
                No projected "Use this card" button any more: the drop-in is tokenized from
                the footer's own "Submit Payment" CTA via SDC's `BraintreeTrigger`
                (`submitPayment()`), so the patron confirms the card once, not twice.
              -->
              <sdc-braintree
                [token]="data.btToken"
                [trigger]="braintreeTrigger"
                (onSubmit)="onSubmit($event)"
                [isDigitalWalletPayment]="
                  paymentDetails.digitalWallet !== null
                ">
              </sdc-braintree>

              <!-- Legal terms / checkbox -->
              <div class="mt-4">
                <app-checkout-recap
                  section="legal"
                  [transaction]="transaction()!"
                  [paymentDetails]="paymentDetails"
                  [isTicketFeeEnabled]="isTicketFeeEnabled()"
                  [isDiscountCodesEnabled]="isDiscountCodesEnabled()" />
              </div>
            </div>
          </form>
        </div>

        <!--
          RIGHT COLUMN — Order Summary sidebar.

          There is no page title spanning both columns — "Your
          Cart" sits above the Ticket details card in the LEFT
          column instead (see above), and "Remaining time" sits
          at the top of THIS column, above the Order Summary card, at the
          same y as the left column's own first row. `<app-timer>` is
          therefore the first child here, not part of a shared title row.

          Sticky column: `lg:sticky` + `lg:top-0` + `lg:self-start` pins this
          column under the header for the whole scroll. `lg:top-0` matches
          the column's natural offset (the global header is `fixed` OUTSIDE
          `#main`/`#checkout`, and `#main` already clears it via its own
          `top-12`/`md:top-16`, so nothing here needs extra top clearance).
          `lg:self-start` stops flex's default `align-items: stretch` from
          growing this column to the left column's full height.

          No `lg:bottom-8`, and no `max-h-[...]`/`overflow-y-auto`
          on the inner wrapper either — both would let the column detach
          from `top-0` and drift upward once the left column's (longer)
          content scrolled past the point where the column's stretched
          height ran out. With the column sized to its own real content
          (discount code + summary card + contact block) instead of being
          stretched or height-capped, it just stays glued to `top-0` for the
          entire scroll — nothing left to detach.
        -->
        <div
          class="flex flex-col gap-5 lg:top-0 lg:sticky lg:self-start w-full lg:w-1/3">
          <app-timer
            (transactionExpired)="onTransactionExpired()"
            [createdTime]="transaction()!.transaction.createdAt"></app-timer>

          <div class="space-y-3">
            @if (!demoDataService.isDemoActive() && isDiscountCodesEnabled()) {
              <app-discount-code
                [transaction]="transaction()!"
                [disabled]="isSelectedUpsellNotEditable"
                (transactionChange)="onDiscountCodeChange($event)" />
            }
            <app-order-summary
              [summaryText]="configurationService.configuration.cartSummaryText"
              [transaction]="transaction()!"
              [totalTransactionSeats]="totalTransactionSeats"
              [upsellSelected]="upsellSelected"
              [rainoutValue]="rainoutValue"
              [isTicketFeeEnabled]="isTicketFeeEnabled()"
              [isDiscountCodesEnabled]="isDiscountCodesEnabled()" />
          </div>

          <!--
            Contact info + secure-checkout badge below the summary card —
            the "NEED ASSISTANCE?" block:
            heading + email/phone as their own icon+link rows in the club
            brand colour
            shared footer (core/layout/footer) carried — reused, not
            reinvented. The badge is
            inlined as an SVG (generic trust badge, not club-branded),
            kept at its native ~81:37 aspect ratio — the old `w-16 h-16`
            square stretched it.
          -->
          <div
            class="flex sm:flex-row flex-col justify-between items-center gap-4 px-2 w-full">
            <div class="flex flex-col items-center sm:items-start gap-3">
              <h5 class="font-bold text-black text-sm uppercase">
                Need Assistance?
              </h5>
              <!--
                `text-main` (`rgb(var(--default-color))`) instead of a fixed
                blue — brand colour must follow the club theme,
                not a fixed placeholder shared by every tenant.
              -->
              <a
                href="mailto:{{ configurationService.configuration.email }}"
                class="flex items-center gap-2 font-bold text-main text-xs">
                <span class="flex w-5 flex-shrink-0 justify-center">
                  <img src="assets/img/email-icon.svg" alt="" class="w-5 h-3" />
                </span>
                {{ configurationService.configuration.email }}
              </a>
              <a
                href="tel:{{ configurationService.configuration.phone }}"
                class="flex items-center gap-2 font-bold text-main text-xs">
                <span class="flex w-5 flex-shrink-0 justify-center">
                  <img src="assets/img/phone-icon.svg" alt="" class="w-3 h-5" />
                </span>
                call {{ configurationService.configuration.phone }}
              </a>
            </div>
            <svg
              class="flex-shrink-0 w-20 h-auto"
              width="81"
              height="37"
              viewBox="0 0 81 37"
              fill="none"
              aria-hidden="true"
              role="img">
              <title>Secure checkout</title>
              <g clip-path="url(#secure-payment-badge-clip)">
                <path
                  d="M1.44189 14.7998V23.4832C1.44189 30.2623 6.935 35.7572 13.7146 35.7665V14.7998H1.44189Z"
                  fill="#9ABD3A" />
                <path
                  d="M13.7144 14.7998V35.7665C13.7144 35.7665 13.7227 35.7665 13.7276 35.7665C19.821 35.7665 24.7598 30.2808 24.7598 23.5127V14.7998H13.7144Z"
                  fill="#8BAF36" />
                <path
                  d="M18.5781 18.5L12.6036 24.4296L9.06031 20.913L6.35107 23.6019L12.3946 29.6L13.8909 28.1132L15.1039 26.9111L21.0783 20.9815L18.5781 18.5Z"
                  fill="white" />
                <path
                  d="M33.5253 23.3729H36.0405V24.2577H31.9209V23.6355C33.1345 22.6417 34.9226 20.9449 34.9226 19.2562C34.9226 18.424 34.2816 18.1816 33.6211 18.1614C32.9996 18.1513 32.1828 18.3028 32.1828 18.3028V17.4302C32.1828 17.4302 32.7945 17.2686 33.6211 17.2686C35.0594 17.2686 35.933 18.0018 35.933 19.2178C35.9721 20.8843 34.6217 22.3912 33.5234 23.375L33.5253 23.3729Z"
                  fill="#8A90A0" />
                <path
                  d="M37.5591 21.0036L36.6465 20.8541L36.8224 17.3999H40.6781V17.5413L40.5511 18.2847H37.7917L37.7037 20.1309C37.9754 20.0703 38.4131 19.9895 38.8978 19.9895C40.2579 19.9895 41.1022 21.0237 41.1022 22.0883C41.1022 23.474 40.2462 24.3769 38.3428 24.3769C37.5161 24.3769 36.6719 24.1264 36.6719 24.1264V23.2841C36.6719 23.2841 37.7213 23.5043 38.3428 23.4841C39.6248 23.4538 40.0918 22.9811 40.0918 22.0883C40.0918 21.3651 39.6248 20.8743 38.8098 20.8743C38.1884 20.8743 37.6627 21.0056 37.6627 21.0056H37.5552L37.5591 21.0036Z"
                  fill="#8A90A0" />
                <path
                  d="M42.632 20.1592C42.9525 20.0481 43.5739 19.8683 44.3419 19.8683C45.9346 19.8784 46.8961 20.6925 46.8961 22.0782C46.8961 23.4639 45.8369 24.3769 44.4006 24.3769C42.6515 24.3769 41.5825 23.173 41.5825 20.8218C41.5825 18.4705 42.9231 17.2666 44.5159 17.2666C45.4285 17.2666 46.0617 17.408 46.0617 17.408V18.313C46.0617 18.313 45.2741 18.1312 44.5159 18.1615C43.6716 18.2019 42.8157 18.6846 42.632 20.1592ZM45.8858 22.0782C45.8858 21.1934 45.3034 20.7935 44.34 20.7632C43.6404 20.7531 42.9407 20.9652 42.5909 21.0844C42.6495 22.9023 43.3589 23.4942 44.3986 23.4942C45.1666 23.4942 45.8858 22.9609 45.8858 22.0782Z"
                  fill="#8A90A0" />
                <path
                  d="M53.6911 17.2789C55.1489 17.2789 56.1593 17.8101 56.1593 19.1958C56.1593 19.9594 55.7118 20.4704 55.0512 20.7532C55.7802 20.9734 56.2844 21.4562 56.2844 22.3692C56.2844 23.9266 55.1177 24.3771 53.8064 24.3771C52.9719 24.3771 51.7661 24.2559 51.7661 24.2559V17.3981C51.7661 17.3981 52.6299 17.2769 53.6891 17.2769L53.6911 17.2789ZM53.1575 20.3513H53.6911C54.3223 20.3513 55.1489 20.0887 55.1489 19.1958C55.1489 18.303 54.3047 18.1717 53.6911 18.1717C53.4487 18.1717 53.1185 18.1919 52.798 18.2121V23.4439C53.1966 23.4641 53.6031 23.4843 53.8083 23.4843C54.2852 23.4843 55.276 23.4237 55.276 22.3692C55.276 21.4845 54.4415 21.2441 53.8083 21.2441H53.1575V20.3513Z"
                  fill="#8A90A0" />
                <path
                  d="M58.0846 17.3999V24.2577H57.0547V17.3999H58.0846Z"
                  fill="#8A90A0" />
                <path
                  d="M63.4783 17.4V17.5494L63.3415 18.2827H61.6315V24.2578H60.6017V18.2827H58.7549V18.1312L58.8917 17.3979H63.4764L63.4783 17.4Z"
                  fill="#8A90A0" />
                <path
                  d="M68.7273 17.2686C69.5617 17.2686 70.2223 17.4605 70.2223 17.4605V18.3149C70.2223 18.3149 69.3487 18.1533 68.7273 18.1634C67.7658 18.1937 67.2889 18.525 67.2889 19.1088C67.2889 19.6925 67.6974 19.9713 68.6296 20.143C69.8334 20.3733 70.9805 20.6763 70.9805 22.2619C70.9805 23.4861 70.1539 24.3809 68.4068 24.3809C67.5723 24.3809 66.6089 24.1304 66.6089 24.1304V23.2881C66.6089 23.2881 67.7756 23.5083 68.4068 23.4881C69.3683 23.4679 69.9702 23.1467 69.9702 22.264C69.9702 21.3812 69.0771 21.2903 68.4439 21.1691C67.6192 21.0096 66.2297 20.6783 66.2786 19.0704C66.3079 18.0058 67.1326 17.2726 68.7273 17.2726V17.2686Z"
                  fill="#8A90A0" />
                <path
                  d="M73.9079 17.2686C74.7424 17.2686 75.403 17.4605 75.403 17.4605V18.3149C75.403 18.3149 74.5294 18.1533 73.9079 18.1634C72.9465 18.1937 72.4696 18.525 72.4696 19.1088C72.4696 19.6925 72.8781 19.9713 73.8102 20.143C75.0141 20.3733 76.1612 20.6763 76.1612 22.2619C76.1612 23.4861 75.3346 24.3809 73.5875 24.3809C72.753 24.3809 71.7895 24.1304 71.7895 24.1304V23.2881C71.7895 23.2881 72.9562 23.5083 73.5875 23.4881C74.5489 23.4679 75.1509 23.1467 75.1509 22.264C75.1509 21.3812 74.2578 21.2903 73.6246 21.1691C72.7999 21.0096 71.4104 20.6783 71.4593 19.0704C71.4886 18.0058 72.3152 17.2726 73.9079 17.2726V17.2686Z"
                  fill="#8A90A0" />
                <path
                  d="M80.7205 23.5245L80.5856 24.2577H76.9429V17.3999H77.963V23.375H80.7224V23.5245H80.7205Z"
                  fill="#8A90A0" />
                <path
                  d="M35.6281 31.8345L35.4933 32.5678H31.9092V25.71H35.6301V25.8594L35.4952 26.5927H32.941V28.5097H34.8836V29.3944H32.941V31.683H35.632V31.8325L35.6281 31.8345Z"
                  fill="#8A90A0" />
                <path
                  d="M41.2758 25.71V32.5678H39.7808L37.3809 27.437L37.4298 32.5678H36.3999V25.71H37.6135L40.2947 31.4629L40.2459 25.71H41.2758Z"
                  fill="#8A90A0" />
                <path
                  d="M44.7467 25.5787C45.5733 25.5787 46.3589 25.7888 46.3589 25.7888V26.6432C46.3589 26.6432 45.3681 26.4634 44.7467 26.4715C43.7852 26.5018 43.0562 27.124 43.0562 29.1338C43.0562 31.1437 43.6875 31.7659 44.8053 31.7942C45.4268 31.8144 46.4176 31.5942 46.4176 31.5942V32.4365C46.4176 32.4365 45.63 32.687 44.8053 32.687C42.9019 32.687 42.0459 31.4831 42.0459 29.1318C42.0459 26.7806 42.9976 25.5767 44.7467 25.5767V25.5787Z"
                  fill="#8A90A0" />
                <path
                  d="M49.257 25.5889C50.7246 25.5889 51.8405 26.3019 51.8405 27.6876C51.8405 28.6916 51.0725 29.4652 50.0133 29.7157L51.8874 32.3376V32.5679H50.8282L48.9638 29.9177V29.0249H49.255C49.896 29.0249 50.8282 28.7441 50.8282 27.6896C50.8282 26.6352 49.8667 26.4857 49.255 26.4857C49.0225 26.4857 48.6824 26.5059 48.3619 26.5261V32.5699H47.3418V25.7121C47.3418 25.7121 48.2056 25.5909 49.255 25.5909L49.257 25.5889Z"
                  fill="#8A90A0" />
                <path
                  d="M53.8752 32.5678V30.2085L51.9131 25.8413V25.71H52.9821L54.3911 29.1338L55.8099 25.71H56.8789V25.8413L54.907 30.2186V32.5678H53.8771H53.8752Z"
                  fill="#8A90A0" />
                <path
                  d="M58.8211 29.5643H59.4132C60.2008 29.5643 61.1622 29.4229 61.1622 28.0473C61.1622 26.6716 60.2301 26.4818 59.4132 26.4818C59.122 26.4818 58.8308 26.502 58.5787 26.5222V32.566H57.5488V25.7081C57.5488 25.7081 58.4322 25.5869 59.4132 25.5869C61.0352 25.5869 62.1726 26.3404 62.1726 28.0473C62.1726 29.7541 61.0059 30.4571 59.4132 30.4571H58.8211V29.5643Z"
                  fill="#8A90A0" />
                <path
                  d="M67.1307 25.71V25.8595L66.9939 26.5928H65.2839V32.5679H64.254V26.5928H62.4072V26.4413L62.544 25.708H67.1287L67.1307 25.71Z"
                  fill="#8A90A0" />
                <path
                  d="M68.8424 25.71V32.5678H67.8125V25.71H68.8424Z"
                  fill="#8A90A0" />
                <path
                  d="M75.0124 29.1338C75.0124 31.483 74.0607 32.6889 72.3116 32.6889C70.5626 32.6889 69.6108 31.485 69.6108 29.1338C69.6108 26.7825 70.5626 25.5786 72.3116 25.5786C74.0607 25.5786 75.0124 26.7845 75.0124 29.1338ZM74.0021 29.1338C74.0021 27.1259 73.2536 26.4714 72.3116 26.4714C71.3697 26.4714 70.6212 27.1239 70.6212 29.1338C70.6212 31.1437 71.3599 31.7941 72.3116 31.7941C73.2633 31.7941 74.0021 31.1517 74.0021 29.1338Z"
                  fill="#8A90A0" />
                <path
                  d="M80.6601 25.71V32.5678H79.165L76.7652 27.437L76.8141 32.5678H75.7842V25.71H76.9978L79.679 31.4629L79.6302 25.71H80.6601Z"
                  fill="#8A90A0" />
                <path
                  d="M48.0591 20.6826H50.6055V21.7471H48.0591V20.6826Z"
                  fill="#8A90A0" />
                <path
                  d="M12.4057 5.83721C8.92218 6.2842 6.3069 9.22868 6.30157 12.723L6.29802 14.8001H2.66895L2.6725 13.092C2.54999 7.32896 7.1325 2.62311 12.8229 2.47057C18.5346 2.31802 23.3852 6.81101 23.5273 12.5971L23.5326 14.8001H19.9514L19.9479 12.3878C19.9426 8.38259 16.3969 5.32458 12.4057 5.83543V5.83721Z"
                  fill="#A2A6B1" />
                <g clip-path="url(#secure-payment-badge-clip-2)">
                  <path
                    d="M35.96 3.7002C37.2891 3.7002 38.352 3.99936 38.352 3.99936V5.69247C38.352 5.69247 36.9605 5.44102 35.96 5.47326C34.6469 5.50549 34.1161 6.02258 34.1001 6.64928C34.0854 7.44877 34.5072 7.73117 35.7884 7.91943C37.5539 8.1696 39.5241 8.68797 39.5241 11.3688C39.5241 13.2812 38.0553 14.8015 35.2416 14.8015C33.9125 14.8015 32.3653 14.4095 32.3653 14.4095V12.7009C32.3653 12.7009 34.2411 13.0452 35.2416 13.0297C36.7888 12.982 37.3369 12.2612 37.3369 11.3676C37.3369 10.5513 36.4296 10.2225 35.4132 10.0652C34.3968 9.9246 31.8332 9.58031 31.9104 6.5848C31.9582 4.92264 33.3964 3.7002 35.96 3.7002Z"
                    fill="#212121" />
                  <path
                    d="M46.7492 3.88965V4.12434L46.4685 5.66013H42.9828V7.98123H45.5465V9.753H42.9828V12.8581H46.8436V13.076L46.5629 14.6131H40.7637V3.88965H46.7505H46.7492Z"
                    fill="#212121" />
                  <path
                    d="M51.8179 3.7002C53.147 3.7002 54.4135 4.02902 54.4135 4.02902V5.73889C54.4135 5.73889 52.8184 5.43973 51.8179 5.47197C50.2707 5.5042 49.5669 6.61704 49.5669 9.2502C49.5669 11.8834 50.1137 12.982 51.9123 13.0284C52.9128 13.0439 54.5067 12.6996 54.5067 12.6996V14.4082C54.5067 14.4082 53.2401 14.8002 51.9123 14.8002C48.8484 14.8002 47.3784 12.9201 47.3784 9.25148C47.3784 5.58286 49.0042 3.7002 51.8179 3.7002Z"
                    fill="#212121" />
                  <path
                    d="M64.4681 3.88965V10.2856C64.4681 13.2024 62.9367 14.8001 60.1229 14.8001C57.3092 14.8001 55.7778 13.2024 55.7778 10.2856V3.88965H57.981V10.2856C57.981 12.1669 58.5904 13.0296 60.1229 13.0296C61.6555 13.0296 62.2489 12.1669 62.2489 10.2856V3.88965H64.4681Z"
                    fill="#212121" />
                  <path
                    d="M69.3035 3.7002C71.6477 3.7002 73.4623 4.8298 73.4623 6.99228C73.4623 8.45071 72.3834 9.50036 70.852 9.90913L73.8215 14.2522V14.6119H71.5386L68.8192 10.4739V8.68668H69.3035C70.3199 8.68668 71.2726 8.12188 71.2726 6.99228C71.2726 5.86268 70.288 5.48744 69.3035 5.48744C69.0534 5.48744 68.7567 5.5042 68.4281 5.51968V14.6119H66.209V3.88975C66.209 3.88975 67.6005 3.7002 69.3035 3.7002Z"
                    fill="#212121" />
                  <path
                    d="M80.9055 3.88965V4.12434L80.6247 5.66013H77.1391V7.98123H79.7027V9.753H77.1391V12.8581H80.9999V13.076L80.7191 14.6131H74.9199V3.88965H80.9067H80.9055Z"
                    fill="#212121" />
                </g>
              </g>
              <defs>
                <clipPath id="secure-payment-badge-clip">
                  <rect width="81" height="37" fill="white" />
                </clipPath>
                <clipPath id="secure-payment-badge-clip-2">
                  <rect
                    width="49.0909"
                    height="11.1"
                    fill="white"
                    transform="translate(31.9092 3.7002)" />
                </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      </div>

      <app-footer />
    </div>
  </div>

  <app-sticky-progress-bar
    [currentStep]="2"
    ctaLabel="Checkout"
    ctaDataCy="pay-button"
    [ctaShowLockIcon]="true"
    [ctaDisabled]="
      configurationService.configuration.rainout &&
      rainoutValue === null &&
      eventDate - nowDate > deadlineInMilliseconds
    "
    [ctaTooltip]="
      configurationService.configuration.rainout &&
      rainoutValue === null &&
      eventDate - nowDate > deadlineInMilliseconds
        ? 'Please select a Rainout Insurance option.'
        : ''
    "
    (ctaClick)="submitPayment()">
    <span data-cy="checkout-sticky-bar-total" class="font-bold">
      Your Cart: ${{
        transaction()!.transaction.tdcTransaction?.balance | number: '1.2-2'
      }}
    </span>
  </app-sticky-progress-bar>
}
